import { Suspense, useContext, useEffect, useRef, useState } from 'react';
import { Divider } from 'primereact/divider';
import { useDebounce } from 'primereact/hooks';
import { useWindowSize } from 'react-use';
import './OrgStaff.scss';
import { lazy } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { CommonCtx } from '../../../../context/CommonCtxProvider';
import { AuthCtx } from '../../../../context/AuthCtxProvider';
import { MOBILE_BREAKPOINT, OrgPermissionKeys, OrgSubPermissionKeys } from '../../../../constants/constants';
import { _DatatablePageEvent, _OrgStaff, _Pagination, _ToastMessageProps } from '../../../../constants/staticTypes';
import { defaultPaginationEvent, defaultToastData } from '../../../../helpers/commonHelper';
import MlxToast from '../../../../components/common/MlxToast/MlxToast';
import logger from '../../../../utils/logger';
import MlxButton from '../../../../components/common/MlxButton/MlxButton';
import MlxSearch from '../../../../components/common/MlxSearch/MlxSearch';
import OrgStaffList from './components/OrgStaffList';
import withSidebar from '../../../../hoc/withSideBar/withSideBar';
import { _AddOrgStaffFormType, getOrgStaffList } from '../../../../helpers/orgStaffHelper';
import { ORG_STAFF_LIST } from '../../../../constants/queryKeys';
import { checkActionSubPermission } from '../../../../utils/common';

const AddOrgStaffSideBar = lazy(() => import('./components/AddOrgStaffSideBar'));

const searchColumns = ['firstName', 'lastName', 'email'];

function OrgStaff() {
    const { width: screenWidth } = useWindowSize();
    const { showSideBar, setShowSideBar } = useContext(CommonCtx);
    const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce('', 1200);
    const queryClient = useQueryClient();
    const { userType } = useContext(AuthCtx);

    const [searchBarVisible, setSearchBarVisible] = useState(screenWidth > MOBILE_BREAKPOINT);
    const [orgStaffAdded, setOrgStaffAdded] = useState<boolean>(false);
    const [isOrgStaffEdit, setIsOrgStaffEdit] = useState<boolean>(false);
    const [orgStaffEditData, setOrgStaffEditData] = useState<_AddOrgStaffFormType>();
    const [dataTableEvent, setDataTableEvent] = useState<_DatatablePageEvent>(defaultPaginationEvent);
    const [orgStaffListData, setOrgStaffListData] = useState<any>(null);
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);

    const orgStaffListQuery = useQuery({
        queryKey: ORG_STAFF_LIST,
        queryFn: () => getOrgStaffList(queryClient, (dataTableEvent.page ?? 0) + 1, dataTableEvent.rows, dataTableEvent.search),
        onSuccess: (response) => {
            const paginationData = response?.data?.data?.pagination as _Pagination;
            const responseData = response?.data?.data?.lists as _OrgStaff;
            setOrgStaffListData(responseData);
            setDataTableEvent((event) => ({ ...event, totalRows: paginationData.total }));
        },
        onError: (error) => logger.log('Failed to fetch staff list', error),
        refetchOnWindowFocus: false,
    });

    const orgStaffListQueryRef = useRef(orgStaffListQuery);

    useEffect(() => {
        orgStaffListQueryRef.current.refetch();
    }, [dataTableEvent?.forceUpdate]);

    useEffect(() => {
        const searchPayload = searchColumns.map((column) => `&search[${column}]=${debouncedSearchValue}`).join('');
        setDataTableEvent((event) => ({
            ...event,
            search: debouncedSearchValue.trim().length > 0 ? searchPayload : '',
            forceUpdate: event.forceUpdate + 1,
        }));
    }, [debouncedSearchValue]);

    useEffect(() => {
        if (orgStaffAdded) {
            orgStaffListQuery.refetch();
            setOrgStaffAdded(false);
        }
    }, [orgStaffAdded, orgStaffListQuery]);

    const openEditAdminStaffSidebar = (editableData: _AddOrgStaffFormType) => {
        setOrgStaffEditData(editableData);
        setIsOrgStaffEdit(true);
        setShowSideBar(true);
    };

    return (
        <>
            <MlxToast
                message={toastMessage.message}
                show={toastMessage.show}
                variant={toastMessage.variant}
                onClose={() => setToastMessage(defaultToastData)}
            />
            <Suspense fallback={<div>Loading...</div>}>
                <AddOrgStaffSideBar
                    showSideBar={showSideBar}
                    setShowSideBar={setShowSideBar}
                    setOrgStaffAdded={setOrgStaffAdded}
                    isOrgStaffEdit={isOrgStaffEdit}
                    setIsOrgStaffEdit={setIsOrgStaffEdit}
                    orgStaffEditData={orgStaffEditData}
                />
            </Suspense>

            <div className="page-section">
                <span className="page-header">Manage Staff</span>
                <div className="page-content h-100">
                    <div className="content-header">
                        <span className="header-title">{searchBarVisible ? 'Staff List' : ''}</span>
                        <div className="header-fields">
                            {userType === 'organizationAdmin' ||
                            checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_STAFF, OrgSubPermissionKeys.ADD_STAFF) ? (
                                <MlxButton
                                    className="ms-2"
                                    removeLabelOnMobile={true}
                                    size="small"
                                    label="Add Staff"
                                    icon="ri-add-large-line"
                                    iconPos="right"
                                    onClick={() => setShowSideBar(true)}
                                />
                            ) : (
                                <></>
                            )}
                            <MlxSearch
                                className="ms-2 p-inputtext-sm"
                                removeLabelOnMobile={true}
                                placeholder="Search"
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                                searchBarVisible={setSearchBarVisible}
                                isLoading={orgStaffListQuery?.isFetching && !!debouncedSearchValue.trim().length}
                            />
                        </div>
                    </div>
                    <Divider className="content-divider" />
                    <div className="content-body overflow-auto">
                        <OrgStaffList
                            dataTableEvent={dataTableEvent}
                            setDataTableEvent={setDataTableEvent}
                            openEditAdminStaffSidebar={openEditAdminStaffSidebar}
                            adminStaffListData={orgStaffListData}
                            setOrgStaffListData={setOrgStaffListData}
                            adminStaffListQuery={orgStaffListQuery}
                            setToastMessage={setToastMessage}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default withSidebar(OrgStaff);
